import { Link } from "react-router-dom";
import {
  Leaf, PenTool, Hotel, Flower2, Ship, Car, Map, Sparkles, Check,
} from "lucide-react";
import { Reveal, Overline, SectionHeading } from "@/components/Primitives";
import { InnerHero, CTABand } from "@/components/Sections";
import { IMG, B2B_OFFERINGS, PROCESS, PARTNERS } from "@/data/site";

const ICONS = { Leaf, PenTool, Hotel, Flower2, Ship, Car, Map, Sparkles };

const WHY = [
  "Warm, responsive people who genuinely care about your trip",
  "Handpicked boutique and luxury stays with real character",
  "Genuine, non-touristy cultural and wellness experiences",
  "Honest, transparent pricing with no hidden surprises",
  "A single, dedicated point of contact throughout",
  "Deep local roots — journeys designed by people who live here",
];

export default function Partnership() {
  return (
    <>
      <InnerHero
        overline="The Travel Bug Way"
        title="Journeys crafted by hand, with heart"
        subtitle="We design each journey around you — with local expertise, thoughtful detail and authentic experiences that stay with you long after you return home."
        image={IMG.hotelWater}
      />

      {/* Who we craft for */}
      <section className="bg-ivory py-20 md:py-28" data-testid="partners-who">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><SectionHeading overline="Who we craft for" title="Journeys for every kind of traveller" /></Reveal>
          <div className="mt-10 md:mt-12 flex flex-wrap gap-x-3 gap-y-4">
            {PARTNERS.map((p, i) => (
              <span key={p} className="inline-flex min-h-11 items-center rounded-full border border-sand bg-coconut px-5 py-2.5 text-sm text-navy/75" data-testid={`partner-type-${i}`}>{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* What every journey includes */}
      <section className="bg-sand/30 py-24 md:py-32" data-testid="partners-offer">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><SectionHeading overline="What's included" title="Everything a great journey needs" /></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {B2B_OFFERINGS.map((o, i) => {
              const Icon = ICONS[o.icon];
              return (
                <Reveal key={o.title} delay={(i % 4) * 0.07}>
                  <div className="h-full rounded-2xl border border-sand bg-ivory p-7" data-testid={`offering-${i}`}>
                    <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center text-teal"><Icon size={20} /></div>
                    <h3 className="mt-5 font-serif text-xl text-forest leading-tight">{o.title}</h3>
                    <p className="mt-2 text-sm text-navy/60 font-light leading-relaxed">{o.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why travellers choose us */}
      <section className="bg-forest text-ivory grain-dark relative" data-testid="partners-why">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Overline className="text-gold">Why travellers choose us</Overline>
            <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Local knowledge, thoughtful care
            </h2>
            <p className="mt-6 text-lg font-light text-ivory/75 leading-relaxed">
              We travel the way we'd want to be looked after — combining deep local knowledge
              with a genuine, personal touch at every step of the journey.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-4">
              {WHY.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center text-gold shrink-0"><Check size={14} /></span>
                  <span className="text-ivory/85 font-light">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="bg-ivory py-24 md:py-32" data-testid="partners-process">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><SectionHeading overline="How we plan your journey" title="Simple, personal, effortless" align="center" className="mx-auto" /></Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-sand bg-coconut p-8" data-testid={`process-${i}`}>
                  <span className="font-serif text-5xl text-gold/50">{p.step}</span>
                  <h3 className="mt-4 font-serif text-2xl text-forest">{p.title}</h3>
                  <p className="mt-2 text-sm text-navy/65 font-light leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        overline="Start your journey"
        title="Ready to travel differently?"
        subtitle="Let's craft a journey you'll remember — unhurried, authentic and entirely your own."
        primary={{ label: "Plan Your Journey", to: "/contact" }}
        secondary={{ label: "See Sample Itineraries", to: "/itineraries" }}
      />
    </>
  );
}
